import { CANVAS_SIZE } from "../constants/canvas";
import type { Layer, HistoryEntry } from "../types";
import type PixelCanvas from "../components/canvas/PixelCanvas.vue";
import { useAvatarStore } from "../stores/useAvatarStore";

// 计算两个图层之间的差异
function calculateLayerDiff(currentLayer: Layer, previousLayer: Layer) {
  const diff: { index: number; value: string }[] = [];

  for (let i = 0; i < currentLayer.pixels.length; i++) {
    if (currentLayer.pixels[i] !== previousLayer.pixels[i]) {
      diff.push({ index: i, value: currentLayer.pixels[i] });
    }
  }

  return diff;
}

// 应用差异到图层
function applyLayerDiff(
  baseLayer: Layer,
  diff: { index: number; value: string }[]
): Layer {
  const newPixels = [...baseLayer.pixels];
  diff.forEach(({ index, value }) => {
    newPixels[index] = value;
  });

  return {
    ...baseLayer,
    pixels: newPixels,
  };
}

export function useMenuActions() {
  // 使用Pinia store
  const store = useAvatarStore();

  const handleImport = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (typeof result !== "string") return;

        const importedData = JSON.parse(result);

        // Validate data format
        if (!importedData.version || !importedData.initialState) {
          throw new Error("Invalid data format");
        }

        // Validate canvas size
        if (
          importedData.canvas?.width !== CANVAS_SIZE ||
          importedData.canvas?.height !== CANVAS_SIZE
        ) {
          throw new Error(
            `Unsupported canvas size. Expected ${CANVAS_SIZE}x${CANVAS_SIZE}`
          );
        }

        // 设置初始状态
        store.layers = importedData.initialState.layers.map((layer: Layer) => ({
          id: layer.id,
          name: layer.name,
          visible: layer.visible,
          pixels: [...layer.pixels],
          requiresPrevious: layer.requiresPrevious,
        }));

        // 重建历史记录
        if (
          importedData.historyDiffs &&
          Array.isArray(importedData.historyDiffs)
        ) {
          let currentState = importedData.initialState.layers;

          store.history = importedData.historyDiffs.map(
            (entry: HistoryEntry) => {
              // 如果是完整状态记录
              if (entry.layers) {
                currentState = entry.layers;
                return {
                  timestamp: entry.timestamp,
                  prompt: entry.prompt,
                  strength: entry.strength,
                  lineWeight: entry.lineWeight,
                  imageUrl: entry.imageUrl,
                  tool: entry.tool,
                  extractedPrompt: entry.extractedPrompt,
                  layers: entry.layers.map((layer: Layer) => ({
                    ...layer,
                    pixels: [...layer.pixels],
                  })),
                };
              }

              // 如果是差异记录
              const newState = currentState.map(
                (layer: Layer, index: number) => {
                  if (!entry.layerDiffs) return layer;

                  const layerDiff = entry.layerDiffs[index];
                  if (!layerDiff) return layer;

                  return {
                    ...layer,
                    visible: layerDiff.visible,
                    pixels: applyLayerDiff(layer, layerDiff.diff).pixels,
                  };
                }
              );

              currentState = newState;

              return {
                timestamp: entry.timestamp,
                prompt: entry.prompt,
                strength: entry.strength,
                lineWeight: entry.lineWeight,
                imageUrl: entry.imageUrl,
                tool: entry.tool,
                extractedPrompt: entry.extractedPrompt,
                layers: currentState.map((layer: Layer) => ({
                  ...layer,
                  pixels: [...layer.pixels],
                })),
              };
            }
          );
        } else {
          store.addHistoryEntry();
        }

        alert("Data imported successfully!");
      } catch (error) {
        console.error("Import error:", error);
        alert(
          `Import failed: ${error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    };

    reader.onerror = () => {
      alert("Error reading file");
    };

    reader.readAsText(file);
    target.value = ""; // Clear file input
  };

  const handleExport = () => {
    // 创建初始状态的完整备份
    const initialState = {
      layers: store.layers.map((layer) => ({
        id: layer.id,
        name: layer.name,
        visible: layer.visible,
        pixels: layer.pixels,
        requiresPrevious: layer.requiresPrevious,
      })),
    };

    // 计算每个历史记录的差异
    const historyDiffs = store.history.map((entry, index) => {
      const previousEntry = index > 0 ? store.history[index - 1] : null;

      // 如果是第一个记录，存储完整状态
      if (!previousEntry) {
        return {
          timestamp: entry.timestamp,
          prompt: entry.prompt,
          strength: entry.strength,
          lineWeight: entry.lineWeight,
          imageUrl: entry.imageUrl,
          tool: entry.tool,
          extractedPrompt: entry.extractedPrompt,
          layers: entry.layers.map((layer) => ({
            id: layer.id,
            name: layer.name,
            visible: layer.visible,
            pixels: layer.pixels,
            requiresPrevious: layer.requiresPrevious,
          })),
        };
      }

      // 对于后续记录，只存储差异
      const layerDiffs = entry.layers.map((currentLayer, layerIndex) => {
        const previousLayer = previousEntry.layers[layerIndex];
        return {
          id: currentLayer.id,
          diff: calculateLayerDiff(currentLayer, previousLayer),
          visible: currentLayer.visible,
        };
      });

      return {
        timestamp: entry.timestamp,
        prompt: entry.prompt,
        strength: entry.strength,
        lineWeight: entry.lineWeight,
        imageUrl: entry.imageUrl,
        tool: entry.tool,
        extractedPrompt: entry.extractedPrompt,
        layerDiffs,
      };
    });

    const exportData = {
      version: "1.0",
      canvas: {
        width: CANVAS_SIZE,
        height: CANVAS_SIZE,
      },
      initialState,
      historyDiffs,
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportName =
      "avatar_" +
      new Date().toISOString().slice(0, 19).replace(/[-:]/g, "") +
      ".json";

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportName);
    linkElement.click();
  };

  const handleExportPng = (
    canvasRef: InstanceType<typeof PixelCanvas> | null
  ) => {
    if (!canvasRef) {
      console.warn("Canvas reference is not available");
      return;
    }
    const dataUrl = canvasRef.exportToPng();
    if (!dataUrl) {
      console.warn("Failed to generate PNG data URL");
      return;
    }

    // Create download link
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `avatar_${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[-:]/g, "")}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    handleImport,
    handleExport,
    handleExportPng,
  };
}
